import { ApiClient } from './ApiClient';
import type { PaymentTrackingQuery, PaymentTrackingDto, ApiResponse } from './PaymentTrackingTypes';

export class ApiClientExtended extends ApiClient {
    /**
     * Download file dạng blob từ API endpoint
     */
    async downloadBlob(url: string, method: string = "GET", data?: any): Promise<Blob> {
        const response = await this.instance.request({
            method,
            url: this.baseUrl + url,
            data: data ? JSON.stringify(data) : undefined,
            headers: data ? { "Content-Type": "application/json" } : undefined,
            responseType: "blob",
        });
        return new Blob([response.data]);
    }

    /**
     * Xuất Excel danh sách miễn giảm học phí (trả về Blob)
     */
    async tuitionExemptionExportExcel(filter: any): Promise<Blob> {
        const response = await this.instance.request({
            method: "POST",
            url: this.baseUrl + "/api/v1/tuition-exemption/export-excel",
            data: filter,
            headers: { "Content-Type": "application/json" },
            responseType: "blob",
        });
        return new Blob([response.data]);
    }

    /**
     * Cập nhật hàng loạt thông tin ngân hàng cho miễn giảm học phí
     */
    async tuitionExemptionBulkUpdateBank(formData: FormData): Promise<any> {
        const response = await this.instance.request({
            method: "POST",
            url: this.baseUrl + "/api/v1/tuition-exemption/bulk-update-bank",
            data: formData,
        });
        return response.data;
    }

    /**
     * Lấy thống kê tiến trình nộp học phí
     */
    /** Đợt xác nhận số tiền chi trả (phụ huynh) — admin */
    async tuitionExemptionParentConfirmBatchList(): Promise<any> {
        const response = await this.instance.request({
            method: "GET",
            url: this.baseUrl + "/api/v1/tuition-exemption/parent-confirm/batches",
            headers: { Accept: "text/plain" },
        });
        return response.data;
    }

    async tuitionExemptionParentConfirmBatchSave(body: Record<string, unknown>): Promise<any> {
        const response = await this.instance.request({
            method: "POST",
            url: this.baseUrl + "/api/v1/tuition-exemption/parent-confirm/batches",
            data: body,
            headers: { "Content-Type": "application/json", Accept: "text/plain" },
        });
        return response.data;
    }

    async tuitionExemptionParentConfirmBatchDelete(id: number): Promise<any> {
        const response = await this.instance.request({
            method: "DELETE",
            url: this.baseUrl + "/api/v1/tuition-exemption/parent-confirm/batches/" + id,
            headers: { Accept: "text/plain" },
        });
        return response.data;
    }

    async tuitionExemptionParentConfirmResponseList(batchId: number): Promise<any> {
        const response = await this.instance.request({
            method: "GET",
            url: this.baseUrl + "/api/v1/tuition-exemption/parent-confirm/batches/" + batchId + "/responses",
            headers: { Accept: "text/plain" },
        });
        return response.data;
    }

    /** Công khai — không cần token đăng nhập */
    async tuitionExemptionParentConfirmPublicGet(token: string): Promise<any> {
        const response = await this.instance.request({
            method: "GET",
            url: this.baseUrl + "/api/v1/public/tuition-exemption/parent-confirm/" + token,
            headers: { Accept: "text/plain" },
        });
        return response.data;
    }

    async tuitionExemptionParentConfirmPublicSubmit(token: string, body: { studentCode: string; fullName: string; amountConfirmed: number }): Promise<any> {
        const response = await this.instance.request({
            method: "POST",
            url: this.baseUrl + "/api/v1/public/tuition-exemption/parent-confirm/" + token + "/submit",
            data: body,
            headers: { "Content-Type": "application/json", Accept: "text/plain" },
        });
        return response.data;
    }

    /**
     * Thống kê miễn giảm theo phường — thêm lọc nhiều xã/phường (wardIds).
     */
    async tuitionExemptionStatsByWardFiltered(
        exemptionYear: string | undefined,
        semester: number | undefined,
        provinceId: number | undefined,
        submissionMethod: number | undefined,
        status: number | undefined,
        wardIds?: number[],
        paymentPayout?: number
    ): Promise<any> {
        let url_ = this.baseUrl + "/api/v1/tuition-exemption/stats-by-ward?";
        if (exemptionYear !== undefined)
            url_ += "exemptionYear=" + encodeURIComponent("" + exemptionYear) + "&";
        if (semester !== undefined)
            url_ += "semester=" + encodeURIComponent("" + semester) + "&";
        if (provinceId !== undefined)
            url_ += "provinceId=" + encodeURIComponent("" + provinceId) + "&";
        if (submissionMethod !== undefined)
            url_ += "submissionMethod=" + encodeURIComponent("" + submissionMethod) + "&";
        if (status !== undefined)
            url_ += "status=" + encodeURIComponent("" + status) + "&";
        if (paymentPayout !== undefined)
            url_ += "paymentPayout=" + encodeURIComponent("" + paymentPayout) + "&";
        if (wardIds != null && wardIds.length > 0) {
            for (const w of wardIds) {
                url_ += "wardIds=" + encodeURIComponent("" + w) + "&";
            }
        }
        url_ = url_.replace(/[?&]$/, "");
        const response = await this.instance.request({
            method: "GET",
            url: url_,
            headers: { Accept: "text/plain" },
        });
        return this.processTuitionExemptionStatsByWard(response);
    }

    async receiptGetPaymentTracking(query: PaymentTrackingQuery): Promise<ApiResponse<PaymentTrackingDto>> {
        const url = this.baseUrl + "/api/v1/receipt/payment-tracking";
        
        const options = {
            method: "POST",
            url: url,
            headers: {
                "Content-Type": "application/json",
                "Accept": "text/plain"
            },
            data: JSON.stringify(query)
        };

        try {
            const response = await this.instance.request(options);
            return response.data;
        } catch (error) {
            console.error("Error calling receiptGetPaymentTracking:", error);
            throw error;
        }
    }

    /** Gửi email thông báo tình trạng hồ sơ lưu trữ (CTSV) cho từng sinh viên đã chọn */
    async studentSendDocumentArchiveNotification(studentIds: number[]): Promise<any> {
        const response = await this.instance.request({
            method: "POST",
            url: this.baseUrl + "/api/v1/student/send-document-archive-notification",
            data: { studentIds },
            headers: { "Content-Type": "application/json", Accept: "text/plain" },
        });
        return response.data;
    }
} 